import { ArrowRight, Check, Lock, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router'

import { useSetupState } from '@/domains/setup'
import { QA_SKIP_CERTS_LABEL } from '@/domains/supplier-onboarding'

import {
  type ChecklistItem,
  type ChecklistSection,
  type SectionProgress,
  getSupplierSections,
  SUPPLIER_SECTIONS
} from './checklist-items'

export function OnboardingSectionView({
  section
}: {
  section: ChecklistSection
}) {
  const { setupState } = useSetupState()
  if (!setupState) return null

  const sections = setupState.isSupplier ? getSupplierSections(setupState) : []
  const found = sections.find(s => s.info.id === section)

  if (!found) {
    const info = SUPPLIER_SECTIONS.find(s => s.id === section)
    return (
      <div className='flex w-full flex-col gap-5'>
        <div>
          <h1 className='font-display text-[22px] leading-[1.1] tracking-[-0.4px] break-words text-ink sm:text-[26px]'>
            {info?.title ?? 'Section'}
          </h1>
          <p className='mt-1 text-[13px] text-ink-soft'>
            Pick a company type on the onboarding overview first.
          </p>
        </div>
      </div>
    )
  }

  const qualitySkippedByCert =
    setupState.supplier?.qualityAssessmentSkippedByCert ?? false

  return (
    <SectionBody section={found} qualitySkippedByCert={qualitySkippedByCert} />
  )
}

function SectionBody({
  section,
  qualitySkippedByCert
}: {
  section: SectionProgress
  qualitySkippedByCert: boolean
}) {
  const percent =
    section.total > 0 ? Math.round((section.complete / section.total) * 100) : 0
  const showQaSkipTip =
    section.info.id === 'quality_assessment' &&
    !qualitySkippedByCert &&
    section.complete < section.total

  return (
    <div className='flex w-full flex-col gap-5'>
      <div>
        <h1 className='font-display text-[22px] leading-[1.1] tracking-[-0.4px] break-words text-ink sm:text-[26px]'>
          {section.info.title}
        </h1>
        <p className='mt-1 text-[13px] text-ink-soft'>
          {section.info.description}
        </p>
      </div>

      {showQaSkipTip && (
        <div className='flex items-start gap-3 rounded-2xl border border-accent/40 bg-accent-light/40 p-4'>
          <ShieldCheck className='mt-0.5 size-4 shrink-0 text-accent dark:text-accent-mid' />
          <div className='min-w-0 flex-1'>
            <p className='text-[13px] font-medium text-ink'>
              Skip this section with a qualifying certificate
            </p>
            <p className='mt-0.5 text-[12px] text-ink-soft'>
              Holding {QA_SKIP_CERTS_LABEL} auto-completes the entire Quality
              assessment.{' '}
              <Link
                to='/onboarding/supplier/documents'
                className='font-medium text-accent dark:text-accent-mid hover:underline'
              >
                Add a certificate
              </Link>
              .
            </p>
          </div>
        </div>
      )}

      <div className='rounded-2xl border border-border bg-cream/40 p-4'>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-[12px] font-medium text-ink-muted'>
            {section.complete} of {section.total} complete
          </span>
          <span className='text-[12px] font-medium text-ink-muted'>
            {percent}%
          </span>
        </div>
        <div className='h-1.5 w-full overflow-hidden rounded-full bg-border'>
          <div
            className='h-full rounded-full bg-accent transition-all'
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      <ul className='flex flex-col gap-3'>
        {section.items.map(item => (
          <StepRow key={item.id} item={item} />
        ))}
      </ul>
    </div>
  )
}

function StepRow({ item }: { item: ChecklistItem }) {
  const Icon = item.icon
  const content = (
    <>
      <div
        className={`flex size-6 shrink-0 items-center justify-center rounded-full border ${
          item.complete
            ? 'border-accent bg-accent text-white'
            : 'border-border bg-page'
        }`}
      >
        {item.complete && <Check className='size-3.5' strokeWidth={3} />}
      </div>

      <div className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-light text-accent dark:text-accent-mid sm:size-9'>
        <Icon className='size-4' />
      </div>

      <div className='min-w-0 flex-1'>
        <p
          className={`text-[14px] font-medium ${
            item.complete ? 'text-ink-muted line-through' : 'text-ink'
          }`}
        >
          {item.title}
        </p>
        <p className='mt-0.5 text-[12px] text-ink-soft sm:truncate'>
          {item.description}
        </p>
      </div>

      {item.disabled ? (
        <Lock className='size-4 shrink-0 text-ink-muted' />
      ) : (
        !item.complete && (
          <ArrowRight className='size-4 shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-accent dark:group-hover:text-accent-mid' />
        )
      )}
    </>
  )

  const baseClasses =
    'flex items-center gap-3 rounded-xl border border-border bg-page p-4 sm:gap-4'

  if (item.disabled) {
    return (
      <li className={`${baseClasses} cursor-not-allowed opacity-60`}>
        {content}
      </li>
    )
  }

  return (
    <li>
      <Link
        to={item.ctaHref}
        className={`group ${baseClasses} transition-colors active:bg-cream/60 md:hover:border-accent/40 md:hover:bg-cream/60`}
      >
        {content}
      </Link>
    </li>
  )
}
